<template>
    <confirm-frame
        v-if="hasLogin"
        :order-item-obj="orderItemObj"
        :submit-order-item="submitOrderItem"
        :protocol="protocol"
        back-url="#/crs/detail"
    ></confirm-frame>
</template>

<script>
import {regionMixin} from '../../components/mobile/mixin';
import ConfirmFrame from '../../components/order/confirmFrame.vue';
import {isLogin, skipToLoginPage} from '../../util/helper';
import {CrsTaxpayerType, CrsComType, packageInfo, companyScopeDetailList} from '../data/order';
import client from '../../common/client';

export default {
    mixins: [regionMixin],

    components: {
        ConfirmFrame
    },

    data() {
        return {
            hasLogin: false,
            codeToName: {},
            codeToPinyin: {},
            queryParams: {},
            policy: null, // 价格策略
            orderItemObj: null, // 订单确认使用参数
            submitOrderItem: null, // 提交订单使用参数
            protocol: {
                name: '《百度智能云工商注册服务协议》',
                href: 'https://cloud.baidu.com/doc/CBS/s/skj9vos8w'
            },
            CrsTaxpayerType,
            CrsComType,
            packageInfo
        };
    },

    created() {
        isLogin().then(res => {
            const hasLogin = res && res.result && res.result.hasLogin;
            if (hasLogin) {
                this.hasLogin = true;
                this.initOrderData();
            }
            else {
                skipToLoginPage();
            }
        });
    },

    methods: {
        async initOrderData() {
            await this.getRegion();
            const {nameToCode, nameToPinyin} = this.staticRegionData;
            this.codeToName = this.setCodeToName(nameToCode);
            this.codeToPinyin = this.setCodeToPinyin(nameToCode, nameToPinyin);

            // 从 url 中取出参数
            const queryParams = this.$route.query;
            if (!queryParams.packageType) {
                queryParams.packageType = '0';
            }

            await this.queryPolicy(queryParams);

            this.orderItemObj = this.getOrderItemObj(queryParams);
            this.submitOrderItem = this.getSubmitOrderItem(queryParams);
        },
        setCodeToName(nameToCode) {
            let result = {};
            Object.keys(nameToCode).forEach(key => {
                result[nameToCode[key]] = key;
            });
            return result;
        },
        setCodeToPinyin(nameToCode, nameToPinyin) {
            let result = {};
            Object.keys(nameToPinyin).forEach(key => {
                result[nameToCode[key]] = nameToPinyin[key];
            });
            return result;
        },
        // 根据公司性质和纳税人类型返回 billing 中 flavor 的 value 值
        getFlavorValue(comType, taxpayerType, packageType) {
            if (comType === '集团公司' && packageType === '0') {
                return comType;
            }
            return `${comType}-${taxpayerType}`;
        },
        // 查询策略
        async queryPolicy(options = {}) {
            const {area, packageType} = options;
            const params = {
                areaCode: area,
                bizType: this.packageInfo[packageType].bizType
            };
            const {data} = await client.getCbsPolicy(params);
            this.policy = data.pricingObjectId;
        },
        // 获取查询订单配置参数
        getOrderItemObj(options = {}) {
            const {area, comType, taxpayerType, packageType} = options;
            return {
                serviceType: 'CBS',
                serviceName: this.packageInfo[packageType].serviceName,
                count: 1,
                duration: 1,
                type: 'NEW',
                productType: 'prepay',
                subProductType: 'Project',
                region: 'global',
                timeUnit: 'DAY',
                unitText: '次',
                policy: this.policy ? this.policy : null,
                flavor: [
                    {
                        name: packageType === '0' ? `${this.codeToPinyin[area]}`
                            : `${this.codeToPinyin[area]}_pkg${packageType}`,
                        value: this.getFlavorValue(
                            this.CrsComType[comType],
                            this.CrsTaxpayerType[taxpayerType],
                            packageType
                        ),
                        scale: 1
                    },
                    {
                        name: 'subServiceType',
                        value: this.packageInfo[packageType].subServiceTypeValue,
                        scale: 1
                    },
                    // 拼接 crs 代金券所需要的 flavor 参数
                    ...this.getCrsCouponFlavorList(packageType, area, comType, taxpayerType)
                ],
                configDetail: [
                    {
                        label: '公司类型',
                        value: this.CrsComType[comType]
                    },
                    {
                        label: '纳税人类型',
                        value: this.CrsTaxpayerType[taxpayerType]
                    },
                    {
                        label: '地区',
                        value: this.codeToName[area]
                    },
                    {
                        label: '服务范围',
                        value: this.packageInfo[packageType].serviceRange
                    }
                ]
            };
        },
        // 获取代金券的 flavor 参数
        getCrsCouponFlavorList(packageType, area, comType, taxpayerType) {
            return [
                {
                    name: 'productType',
                    value: this.packageInfo[packageType].productTypeValue,
                    scale: 1
                },
                {
                    name: 'area',
                    value: area,
                    scale: 1
                },
                {
                    name: 'comType',
                    value: this.CrsComType[comType],
                    scale: 1
                },
                {
                    name: 'taxpayerType',
                    value: this.CrsTaxpayerType[taxpayerType],
                    scale: 1
                }
            ];
        },
        // 获取经营范围的内容
        getScopeContent(bizScope) {
            const scopeIndex = companyScopeDetailList.findIndex(item => item.type === bizScope);
            return scopeIndex > -1 ? companyScopeDetailList[scopeIndex].content : bizScope;
        },
        // 获取提交订单参数
        getSubmitOrderItem(options = {}) {
            const {
                area,
                bizScope,
                comName,
                comType,
                description,
                phone,
                solutionId,
                taxpayerType,
                packageType
            } = options;
            const submitOrderItem = {
                bizType: this.packageInfo[packageType].bizType
            };
            if (solutionId) {
                submitOrderItem.solutionId = solutionId;
            }
            const detail = {
                bizType: this.packageInfo[packageType].bizType,
                bizScope: this.getScopeContent(bizScope),
                area,
                comName,
                comType: this.CrsComType[comType],
                description,
                phone,
                taxpayerType: this.CrsTaxpayerType[taxpayerType],
                engagedIndustry: decodeURIComponent(bizScope)
            };
            if (packageType) {
                detail.packageType = packageType;
            }

            submitOrderItem.detail = detail;
            return submitOrderItem;
        }
    }
};
</script>